import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '../../components/ui/avatar';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Separator } from '../../components/ui/separator';
import { ArrowLeft, Package, ShoppingCart, FileText, DollarSign, Globe, Loader2, Mail, Phone, Calendar } from 'lucide-react';

export default function AdminCreatorDetail() {
  const { creatorId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const res = await api.get(`/admin/creators/${creatorId}`);
      setData(res.data);
    } catch (err) {
      console.error('Failed to load creator');
    } finally {
      setLoading(false);
    }
  }, [creatorId]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  if (!data) return <div className="text-center py-20 text-slate-400">Creator not found</div>;

  const { creator, products, recent_orders, landing_pages } = data;

  return (
    <div className="space-y-6 max-w-[1200px]" data-testid="admin-creator-detail">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/creators')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-slate-900">Creator Details</h1>
          <p className="text-sm text-slate-500">{creator.email}</p>
        </div>
      </div>

      {/* Creator Profile Card */}
      <Card className="border-slate-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <Avatar className="h-16 w-16">
              {creator.avatar_url ? <AvatarImage src={creator.avatar_url} /> : null}
              <AvatarFallback className="bg-slate-200 text-slate-600 text-lg">{(creator.username || creator.name)?.[0]?.toUpperCase() || 'C'}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-semibold">{creator.username || creator.name}</h2>
              <p className="text-sm text-slate-500">@{creator.handle}</p>
              <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-slate-500">
                <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{creator.email}</span>
                {creator.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{creator.phone}</span>}
                <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />Joined {new Date(creator.created_at).toLocaleDateString('en-MY', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Products', value: products.length, icon: Package, color: 'text-blue-600 bg-blue-50' },
          { label: 'Landing Pages', value: landing_pages.length, icon: FileText, color: 'text-teal-600 bg-teal-50' },
          { label: 'Orders', value: recent_orders.length, icon: ShoppingCart, color: 'text-amber-600 bg-amber-50' },
          { label: 'Revenue', value: `RM ${recent_orders.filter(o => o.status === 'paid').reduce((s, o) => s + (o.amount || 0), 0).toFixed(2)}`, icon: DollarSign, color: 'text-emerald-600 bg-emerald-50' },
        ].map(s => {
          const Icon = s.icon;
          return (
            <Card key={s.label} className="border-slate-200">
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2">
                  <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${s.color}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-lg font-bold text-slate-900">{s.value}</p>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wide">{s.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Products */}
      <Card className="border-slate-200">
        <CardHeader><CardTitle className="text-base text-slate-900">Products ({products.length})</CardTitle></CardHeader>
        <CardContent>
          {products.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Price</TableHead>
                  <TableHead className="text-right">Sales</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map(p => (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell><Badge variant="secondary" className="text-[10px]">{p.type}</Badge></TableCell>
                    <TableCell className="text-right">RM {(p.price || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right">{p.total_sales || 0}</TableCell>
                    <TableCell>{p.is_published ? <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Published</Badge> : <Badge variant="secondary" className="text-[10px]">Draft</Badge>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : <p className="text-sm text-slate-400 text-center py-4">No products</p>}
        </CardContent>
      </Card>

      {/* Recent Orders */}
      <Card className="border-slate-200">
        <CardHeader><CardTitle className="text-base text-slate-900">Recent Orders ({recent_orders.length})</CardTitle></CardHeader>
        <CardContent>
          {recent_orders.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ref</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recent_orders.slice(0, 20).map(o => (
                  <TableRow key={o.id}>
                    <TableCell className="font-mono text-xs">{o.ref}</TableCell>
                    <TableCell className="text-sm">{o.customer_name}</TableCell>
                    <TableCell className="text-sm">{o.product_name}</TableCell>
                    <TableCell className="text-right font-semibold">RM {(o.amount || 0).toFixed(2)}</TableCell>
                    <TableCell><Badge variant={o.status === 'paid' ? 'default' : 'secondary'}>{o.status}</Badge></TableCell>
                    <TableCell className="text-xs text-slate-400">{o.created_at ? new Date(o.created_at).toLocaleDateString() : '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : <p className="text-sm text-slate-400 text-center py-4">No orders yet</p>}
        </CardContent>
      </Card>

      {/* Landing Pages */}
      <Card className="border-slate-200">
        <CardHeader><CardTitle className="text-base text-slate-900">Landing Pages ({landing_pages.length})</CardTitle></CardHeader>
        <CardContent>
          {landing_pages.length > 0 ? (
            <div className="space-y-2">
              {landing_pages.map(lp => (
                <div key={lp.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                  <div>
                    <p className="text-sm font-medium">{lp.title}</p>
                    <p className="text-xs text-slate-400">{lp.slug ? `/${creator.handle}/${lp.slug}` : `ID: ${lp.id.slice(0, 8)}`}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-[10px] capitalize">{lp.template === 'ai-generated' || lp.ai_generated ? 'AI Builder' : lp.editor_type || 'basic'}</Badge>
                    {lp.is_published ? <Badge className="bg-emerald-100 text-emerald-700 text-[10px]"><Globe className="h-3 w-3 mr-0.5" />Live</Badge> : <Badge variant="secondary" className="text-[10px]">Draft</Badge>}
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-slate-400 text-center py-4">No landing pages</p>}
        </CardContent>
      </Card>
    </div>
  );
}
